import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import { PricingStrategy } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { IPartValue } from 'quotes/types';
import { getCostValiditySchemaFields, getPurchaseCostLabel, TOneTimeSpaRow } from 'quotes/utils';

// The vendor field rewrites part.supplier on a vendor change; the saved vendor until then.
const getIsShipAndDebitVendor = (part: unknown, savedIsShipAndDebitVendor: boolean): boolean =>
  (part as IPartValue | undefined)?.supplier?.isShipAndDebit ?? savedIsShipAndDebitVendor;

export const quotePartUpdateMargin = (
  isAlternateVersionEnabled: boolean,
  isShipAndDebitVendor: boolean,
  savedQuotePart: TOneTimeSpaRow | null = null
) =>
  yup.object().shape({
    margin: numberSchemaWithTransform().label(t('labelMargin')).nullable().required(),
    customMargin: numberSchemaWithTransform()
      .label(t('labelCustomMargin'))
      .when('margin', {
        is: -1,
        then: numberSchemaWithTransform()
          .label(t('labelMargin'))
          .nullable()
          .when('cost', {
            is: (cost) => cost > 0,
            then: numberSchemaWithTransform()
              .nullable()
              .test(
                'cannot-calculate-price',
                t('errorPriceCannotBeCalculated'),
                (value) => value != null && Number(value) !== 100
              ),
          }),
        otherwise: numberSchemaWithTransform().label(t('labelMargin')).nullable(),
      }),
    cost: numberSchemaWithTransform().label(t('labelCost')).nullable(),
    purchaseCost: numberSchemaWithTransform()
      .label(getPurchaseCostLabel(isAlternateVersionEnabled))
      .nullable(),
    isCostingCustom: yup.boolean().nullable(),
    isPricingCustom: yup.boolean().nullable(),
    costSource: yup.object().shape({
      vendorQuoteNumber: yup
        .string()
        .label(t('labelVendorQuoteNumber'))
        .nullable()
        .test(
          'requiredVendorQuoteNumber',
          getEmptyErrorMessage(t('labelVendorQuoteNumber')),
          function (value) {
            const context = this as TestContext & {
              from: { value: Record<string, unknown> }[];
            };

            const parentValue = context.from?.[1]?.value;
            if (!parentValue) {
              return true;
            }

            const required = QuotePartHelper.requiredVendorQuoteNumber({
              isAlternateVersionEnabled,
              isCostingCustom: !!parentValue.isCostingCustom,
              purchaseCost: parentValue.purchaseCost as number,
            });

            return !required || !!value;
          }
        ),
      ...getCostValiditySchemaFields(
        isAlternateVersionEnabled,
        (formValues) => getIsShipAndDebitVendor(formValues.part, isShipAndDebitVendor),
        savedQuotePart
      ),
    }),
    // `default(null)`: an object schema casts an absent value to `{}`, read as a pick.
    selectedSpa: yup.object().nullable().default(null),
    // The line's default agreement, mirrored from the pricing state for a pick save.
    defaultSpaAgreement: yup.object().nullable().default(null),
    // The live cost's vendor quote, mirrored from the pricing state for the cost validity.
    vendorQuoteCost: yup.object().nullable().default(null),
    isReplacementCostCustom: yup.boolean().nullable(),
    replacementCost: numberSchemaWithTransform()
      .label(t('labelReplacementCost'))
      .nullable()
      .when(['isReplacementCostCustom', 'part', 'purchaseCost'], {
        is: (isReplacementCostCustom: boolean, part: IPartValue, purchaseCost: number) =>
          isReplacementCostCustom &&
          QuotePartHelper.requiredReplacementCost({
            isAlternateVersionEnabled,
            isShipAndDebitVendor: getIsShipAndDebitVendor(part, isShipAndDebitVendor),
            purchaseCost,
          }),
        then: numberSchemaWithTransform().label(t('labelReplacementCost')).nullable().required(),
      }),
    replacementCostNumber: yup
      .string()
      .label(t('labelReplacementCostNumber'))
      .nullable()
      .when(['isReplacementCostCustom', 'part', 'purchaseCost', 'replacementCost'], {
        is: (
          isReplacementCostCustom: boolean,
          part: IPartValue,
          purchaseCost: number,
          replacementCost: number
        ) =>
          isReplacementCostCustom &&
          QuotePartHelper.requiredReplacementCostNumber({
            isAlternateVersionEnabled,
            isShipAndDebitVendor: getIsShipAndDebitVendor(part, isShipAndDebitVendor),
            purchaseCost,
            replacementCost,
          }),
        then: yup.string().nullable().required(),
      }),
    shipmentType: yup
      .object()
      .shape({
        id: yup.string().label(t('labelShipmentType')).nullable().required(),
        name: yup.string().label(t('labelShipmentType')).nullable().required(),
      })
      .label(t('labelShipmentType'))
      .nullable()
      .required(),
    corporateLocation: yup
      .object({
        id: numberSchemaWithTransform().label(t('labelShippingLocation')).nullable().required(),
        addressName: yup.string().label(t('labelShippingLocation')).nullable().required(),
      })
      .label(t('labelShippingLocation'))
      .nullable(),
    priceBasedOn: isAlternateVersionEnabled
      ? yup.string().label(t('labelPriceBasedOn')).nullable().required()
      : yup.string().nullable(),
    pricingMargin: numberSchemaWithTransform().label(t('labelPricingMargin')).nullable(),
    trueMargin: numberSchemaWithTransform().nullable(),
    replacementStandardCost: numberSchemaWithTransform().nullable(),
    pricingDate: yup
      .string()
      .label(t('labelPricingDate'))
      .nullable()
      .test('future-date', t('errorPricingDateMustBeFuture'), (value) => {
        if (!value) {
          return true;
        }

        const selectedDate = dayjs(value).startOf('day');
        const today = dayjs().startOf('day');

        return selectedDate.valueOf() > today.valueOf();
      }),
    pricingStrategy: isAlternateVersionEnabled
      ? yup
          .string()
          .label(t('labelPricingStrategy'))
          .oneOf(Object.values(PricingStrategy))
          .nullable()
          .required(getEmptyErrorMessage(t('labelPricingStrategy')))
      : yup.string().nullable(),
  });

// Validates only the bulk form's editable surface (margin, customMargin, and
// priceBasedOn for alternate). Other single-mode fields are display-only or not
// submitted in bulk; validating them here blocks submit on a field the user can't see.
export const quotePartUpdateMarginBulk = (isAlternateVersionEnabled: boolean) =>
  yup.object().shape({
    margin: numberSchemaWithTransform().label(t('labelMargin')).nullable().required(),
    customMargin: numberSchemaWithTransform()
      .label(t('labelCustomMargin'))
      .when('margin', {
        is: -1,
        then: numberSchemaWithTransform()
          .label(t('labelMargin'))
          .nullable()
          .when('cost', {
            is: (cost) => cost > 0,
            then: numberSchemaWithTransform()
              .nullable()
              .test(
                'cannot-calculate-price',
                t('errorPriceCannotBeCalculated'),
                (value) => value != null && Number(value) !== 100
              ),
          }),
        otherwise: numberSchemaWithTransform().label(t('labelMargin')).nullable(),
      }),
    ...(isAlternateVersionEnabled && {
      priceBasedOn: yup.string().label(t('labelPriceBasedOn')).nullable().required(),
    }),
  });
